import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileStack, ArrowRight } from "lucide-react";
import { GatedSection, UpgradeOverlay } from "@/components/dashboard/UpgradeGate";
import { LatestUpdatesPanel } from "@/components/dashboard/LatestUpdatesPanel";
import { DocumentUploadGate } from "@/components/dashboard/DocumentUploadGate";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";

import { useAuth } from "@/contexts/AuthContext";
import { usePageMeta } from "@/hooks/usePageMeta";

import { HeroStats } from "@/components/shipper-dashboard/HeroStats";
import { ActionItemsPanel } from "@/components/shipper-dashboard/ActionItemsPanel";
import { ActiveLoadsBoard } from "@/components/shipper-dashboard/ActiveLoadsBoard";
import { InsightsGrid } from "@/components/shipper-dashboard/InsightsGrid";
import { QuickActions } from "@/components/shipper-dashboard/QuickActions";
import { RecentActivityFeed } from "@/components/shipper-dashboard/RecentActivityFeed";
import { ShipperGettingStarted } from "@/components/shipper-dashboard/ShipperGettingStarted";


/**
 * Shipper Dashboard — TMS command center.
 *
 * Six-section layout:
 *   1. Hero stats (active loads, pending bids, monthly spend, networks)
 *   2. Needs Your Attention — action items
 *   3. Active Loads board (grouped by lifecycle stage)
 *   4. Insights grid — 4 recharts visualizations
 *   5. Quick actions
 *   6. Recent activity feed
 *
 * Future enhancements (not in v1):
 *   - Realtime push for board/activity (current React Query refetch is sufficient)
 *   - Reports page + PDF/CSV export
 *   - Broker performance scores, predictive insights
 *   - GPS/ELD tracking on active loads
 */
export default function ShipperDashboardV2() {
  usePageMeta({
    title: "Shipper Dashboard | LaneRunner",
    description: "LaneRunner shipper command center",
  });
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const firstName = profile?.first_name || "there";

  useEffect(() => {
    const main = document.querySelector("main");
    if (main) main.scrollTop = 0;
  }, []);

  const userId = user?.id || "";


  // Defensive skeleton: if profile hasn't hydrated yet, render a muted shell
  // instead of a blank screen. Root cause (transient null during refresh) is
  // fixed in AuthContext.refreshProfile; this is a belt-and-suspenders guard
  // for any future path that lands here before profile is ready.
  if (!profile) {
    return (
      <div className="flex flex-col gap-3 xl:gap-4 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 xl:gap-4">
          <div className="rounded-lg border bg-card p-4 animate-pulse">
            <div className="h-3 w-32 bg-muted rounded mb-3" />
            <div className="h-5 w-64 bg-muted rounded mb-2" />
            <div className="h-3 w-full max-w-md bg-muted rounded" />
          </div>
          <div className="rounded-lg border bg-card p-4 h-24 animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border bg-card p-4 h-24 animate-pulse" />
          ))}
        </div>
        <div className="rounded-lg border bg-card p-4 h-64 animate-pulse" />
      </div>
    );
  }

  return (
    <>
      <UpgradeOverlay />
      <DocumentUploadGate />
      <div className="flex flex-col gap-3 xl:gap-4 w-full">


        {/* ── Welcome strip ─────────────────────────────── */}
        <GatedSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 xl:gap-4">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-brand-teal-deep">
                Shipper Dashboard
              </p>
              <h2 className="mt-1 text-lg font-bold text-foreground truncate">
                Welcome back, {firstName}
              </h2>
              <p className="mt-1 text-caption text-muted-foreground">
                Post direct loads, manage your private network, and track every shipment in one place.
              </p>
            </div>
            <LatestUpdatesPanel />
          </div>
        </GatedSection>

        {/* ── Getting Started (replaces identity strip) ── */}
        <ErrorBoundary variant="section" label="Getting Started">
          <GatedSection>
            <ShipperGettingStarted />
          </GatedSection>
        </ErrorBoundary>


        {/* ── Section 1 — Hero Stats ─────────────────────── */}
        <ErrorBoundary variant="section" label="Hero Stats">
          <GatedSection>
            <HeroStats userId={userId} />
          </GatedSection>
        </ErrorBoundary>

        {/* ── Section 2 — Action Items ───────────────────── */}
        <ErrorBoundary variant="section" label="Action Items">
          <GatedSection>
            <ActionItemsPanel userId={userId} />
          </GatedSection>
        </ErrorBoundary>

        {/* ── Posting Templates → moved to ATLAS Command Center ── */}
        <ErrorBoundary variant="section" label="Posting Templates">
          <GatedSection>
            <button
              type="button"
              onClick={() => navigate("/lr-direct/atlas")}
              className="w-full rounded-lg border bg-card px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors text-left"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileStack className="h-4 w-4 text-brand-teal-deep flex-shrink-0" />
                <span className="text-sm font-semibold text-foreground">Posting Templates</span>
                <span className="text-xs text-muted-foreground truncate">
                  now live in the ATLAS Command Center
                </span>
              </div>
              <span className="text-xs font-semibold text-brand-teal-deep flex items-center gap-1 flex-shrink-0">
                Manage templates <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </button>
          </GatedSection>
        </ErrorBoundary>


        {/* ── Section 3 — Active Loads Board ─────────────── */}
        <ErrorBoundary variant="section" label="Active Loads">
          <GatedSection>
            <ActiveLoadsBoard userId={userId} />
          </GatedSection>
        </ErrorBoundary>

        {/* ── Section 4 — Insights ───────────────────────── */}
        <ErrorBoundary variant="section" label="Insights">
          <GatedSection>
            <InsightsGrid userId={userId} />
          </GatedSection>
        </ErrorBoundary>

        {/* ── Section 5 — Quick Actions ──────────────────── */}
        <ErrorBoundary variant="section" label="Quick Actions">
          <GatedSection>
            <QuickActions />
          </GatedSection>
        </ErrorBoundary>

        {/* ── Section 6 — Recent Activity ────────────────── */}
        <ErrorBoundary variant="section" label="Recent Activity">
          <GatedSection>
            <RecentActivityFeed userId={userId} />
          </GatedSection>
        </ErrorBoundary>
      </div>
    </>
  );
}
